import React, { useState, useEffect } from "react";
import useAuth from "../hooks/useAuth";
import api from "../services/api";
import CostEstimateCard from "../components/CostEstimateCard";
import ContactUpload from "../components/ContactUpload";
import { errorToUserMessage } from "../services/errorMessages";

// Common US/territory IANA timezones. Every political campaign we serve will
// fall into one of these. Covered by the backend's zoneinfo validator.
const CAMPAIGN_TIMEZONES = [
  { value: "America/New_York", label: "Eastern (New York)" },
  { value: "America/Chicago", label: "Central (Chicago)" },
  { value: "America/Denver", label: "Mountain (Denver)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { value: "America/Anchorage", label: "Alaska" },
  { value: "Pacific/Honolulu", label: "Hawaii" },
];

const DEFAULT_CAMPAIGN = {
  name: "",
  description: "",
  timezone: "America/New_York",
  number_pool_id: "",
  budget_cap: "",
  first_message: "",
};

const statusColors = {
  draft: "bg-gray-100 text-gray-700",
  ready: "bg-yellow-100 text-yellow-700",
  active: "bg-green-100 text-green-700",
  paused: "bg-orange-100 text-orange-700",
  completed: "bg-blue-100 text-blue-700",
  archived: "bg-gray-100 text-gray-500",
};

// ── Per-campaign manager: add a message (script) + upload/attach contacts ──
function CampaignManager({ campaign, onChanged }) {
  const [scripts, setScripts] = useState([]);
  const [newBody, setNewBody] = useState("");
  const [newMedia, setNewMedia] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadScripts = () => {
    api
      .get(`/campaigns/${campaign.id}/scripts`)
      .then((r) => setScripts(r.data.scripts || (Array.isArray(r.data) ? r.data : [])))
      .catch(() => {});
  };
  useEffect(loadScripts, [campaign.id]); // eslint-disable-line

  const addMessage = async () => {
    if (!newBody.trim()) return;
    const media = newMedia.trim();
    if (media && !media.toLowerCase().startsWith("https://")) {
      alert("The image/video URL must start with https:// — Twilio fetches it at send time.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: `Message ${scripts.length + 1}`,
        body: newBody.trim(),
      };
      if (media) payload.media_url = media;
      await api.post(`/campaigns/${campaign.id}/scripts`, payload);
      setNewBody("");
      setNewMedia("");
      loadScripts();
    } catch (err) {
      alert(errorToUserMessage(err, "Failed to add message"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-gray-100 pt-4 grid md:grid-cols-2 gap-6">
      {/* Messages */}
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
          Messages
        </div>
        {scripts.length === 0 && (
          <p className="text-xs text-gray-400 mb-2">
            No message yet. Add one below — use{" "}
            <code className="bg-gray-100 px-1 rounded">{"{first_name}"}</code> to personalize.
          </p>
        )}
        {scripts.map((s) => (
          <div
            key={s.id}
            className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-1.5"
          >
            {s.body}
            {s.media_url && (
              <a
                href={s.media_url}
                target="_blank"
                rel="noreferrer"
                className="block mt-1 text-xs text-blue-600 hover:underline"
              >
                📎 attached media
              </a>
            )}
          </div>
        ))}
        <textarea
          value={newBody}
          onChange={(e) => setNewBody(e.target.value)}
          rows={3}
          placeholder="Type a message… e.g. Hi {first_name}, this is Alex with the campaign…"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none mt-1"
        />
        <input
          type="text"
          value={newMedia}
          onChange={(e) => setNewMedia(e.target.value)}
          placeholder="Image or video URL (optional, https://…)"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1.5"
        />
        <p className="text-[11px] text-gray-400 mt-1">
          Attached media sends as MMS with every message. Images work best
          (carriers resize them); video must be a small MP4 — well under 500 KB.
        </p>
        <button
          onClick={addMessage}
          disabled={busy || !newBody.trim()}
          className="mt-1.5 text-xs font-medium bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {busy ? "Adding…" : "Add message"}
        </button>
      </div>

      {/* Contacts */}
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
          Contacts
        </div>
        <p className="text-xs text-gray-400 mb-2">
          Contacts uploaded here attach to this campaign and are assigned to you, so
          they appear in your Sending Console immediately.
        </p>
        {showUpload ? (
          <ContactUpload
            campaignId={campaign.id}
            onUploadStarted={() => {
              setShowUpload(false);
              if (onChanged) setTimeout(onChanged, 1500);
            }}
            onClose={() => setShowUpload(false)}
          />
        ) : (
          <button
            onClick={() => setShowUpload(true)}
            className="text-xs font-medium bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200"
          >
            ⬆ Upload contacts to this campaign
          </button>
        )}
      </div>
    </div>
  );
}

export default function CampaignDetail() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [pools, setPools] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCampaign, setNewCampaign] = useState(DEFAULT_CAMPAIGN);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const loadCampaigns = () => {
    setLoading(true);
    api
      .get("/campaigns")
      .then((res) => setCampaigns(res.data.campaigns || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  const loadPools = () => {
    api
      .get("/number-pools")
      .then((r) => setPools(r.data.pools || (Array.isArray(r.data) ? r.data : [])))
      .catch(() => {});
  };
  useEffect(() => {
    loadCampaigns();
    loadPools();
  }, []);

  const createCampaign = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        name: newCampaign.name,
        description: newCampaign.description,
        timezone: newCampaign.timezone,
      };
      if (newCampaign.number_pool_id) payload.number_pool_id = Number(newCampaign.number_pool_id);
      if (newCampaign.budget_cap) payload.budget_cap = Number(newCampaign.budget_cap);

      const res = await api.post("/campaigns", payload);
      const camp = res.data.campaign;

      if (newCampaign.first_message.trim() && camp && camp.id) {
        await api.post(`/campaigns/${camp.id}/scripts`, {
          name: "Message 1",
          body: newCampaign.first_message.trim(),
        });
      }

      setNewCampaign(DEFAULT_CAMPAIGN);
      setShowCreateForm(false);
      loadCampaigns();
      if (camp && camp.id) setExpanded(camp.id); // open manager so they can add contacts
    } catch (err) {
      alert(errorToUserMessage(err, "Failed to create campaign"));
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async (campaignId, newStatus) => {
    try {
      await api.put(`/campaigns/${campaignId}/status`, { status: newStatus });
      loadCampaigns();
    } catch (err) {
      alert(errorToUserMessage(err, "Status update failed"));
    }
  };

  const deleteCampaign = async (campaignId, name) => {
    if (
      !window.confirm(
        `Remove campaign "${name}"?\n\nCampaigns with message history are archived (hidden from this list, records kept). Empty campaigns are deleted outright.`
      )
    )
      return;
    try {
      const res = await api.delete(`/campaigns/${campaignId}`);
      if (res.data && res.data.status === "archived") {
        alert("This campaign had message history, so it was archived (hidden) rather than deleted.");
      }
      if (expanded === campaignId) setExpanded(null);
      loadCampaigns();
    } catch (err) {
      alert(errorToUserMessage(err, "Failed to remove campaign"));
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
        >
          {showCreateForm ? "Cancel" : "+ New Campaign"}
        </button>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <form
          onSubmit={createCampaign}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3"
        >
          <input
            type="text"
            placeholder="Campaign name"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={newCampaign.name}
            onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
            required
          />
          <textarea
            placeholder="Description (optional)"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
            rows={2}
            value={newCampaign.description}
            onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
          />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Time zone</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={newCampaign.timezone}
                onChange={(e) => setNewCampaign({ ...newCampaign, timezone: e.target.value })}
              >
                {CAMPAIGN_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-gray-400 mt-1">Enforces 8 AM–9 PM quiet hours.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sending number pool</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={newCampaign.number_pool_id}
                onChange={(e) => setNewCampaign({ ...newCampaign, number_pool_id: e.target.value })}
              >
                <option value="">Select a pool…</option>
                {pools.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-gray-400 mt-1">Required to send. Manage under Numbers.</p>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Message <span className="text-gray-400 font-normal">— use {"{first_name}"} to personalize</span>
            </label>
            <textarea
              placeholder="Hi {first_name}, this is Alex with the campaign. Election Day is Nov 3 — can we count on your vote? Reply STOP to opt out."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
              rows={3}
              value={newCampaign.first_message}
              onChange={(e) => setNewCampaign({ ...newCampaign, first_message: e.target.value })}
            />
          </div>
          <div className="flex items-center gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Budget cap (USD, optional)</label>
              <input
                type="number"
                min="0"
                step="1"
                placeholder="e.g. 500"
                className="w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={newCampaign.budget_cap}
                onChange={(e) => setNewCampaign({ ...newCampaign, budget_cap: e.target.value })}
              />
            </div>
          </div>
          <p className="text-xs text-gray-400">
            You can add more messages and upload contacts right after creating the campaign.
          </p>
          <button
            type="submit"
            disabled={creating}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300"
          >
            {creating ? "Creating…" : "Create Campaign"}
          </button>
        </form>
      )}

      {/* Campaign list */}
      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading campaigns...</div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-gray-400">
          <p>No campaigns yet. Create your first one!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h3 className="font-semibold text-gray-900">{campaign.name}</h3>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        statusColors[campaign.status] || ""
                      }`}
                    >
                      {campaign.status}
                    </span>
                  </div>
                  {campaign.description && (
                    <p className="text-sm text-gray-500 mt-1">{campaign.description}</p>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-400">
                    <span>Created: {new Date(campaign.created_at).toLocaleDateString()}</span>
                    {campaign.contact_count != null && <span>Contacts: {campaign.contact_count}</span>}
                    {campaign.timezone && <span>Time zone: {campaign.timezone}</span>}
                  </div>
                </div>

                <div className="flex items-center space-x-2 flex-shrink-0">
                  {campaign.status === "draft" && (
                    <button
                      onClick={() => updateStatus(campaign.id, "ready")}
                      className="text-xs bg-yellow-50 text-yellow-700 px-3 py-1 rounded-lg hover:bg-yellow-100"
                    >
                      Mark Ready
                    </button>
                  )}
                  {campaign.status === "ready" && (
                    <button
                      onClick={() => updateStatus(campaign.id, "active")}
                      className="text-xs bg-green-50 text-green-700 px-3 py-1 rounded-lg hover:bg-green-100"
                    >
                      Activate
                    </button>
                  )}
                  {campaign.status === "active" && (
                    <>
                      <button
                        onClick={() => updateStatus(campaign.id, "paused")}
                        className="text-xs bg-orange-50 text-orange-700 px-3 py-1 rounded-lg hover:bg-orange-100"
                      >
                        Pause
                      </button>
                      <button
                        onClick={() => updateStatus(campaign.id, "completed")}
                        className="text-xs bg-blue-50 text-blue-700 px-3 py-1 rounded-lg hover:bg-blue-100"
                      >
                        Complete
                      </button>
                    </>
                  )}
                  {campaign.status === "paused" && (
                    <button
                      onClick={() => updateStatus(campaign.id, "active")}
                      className="text-xs bg-green-50 text-green-700 px-3 py-1 rounded-lg hover:bg-green-100"
                    >
                      Resume
                    </button>
                  )}
                  <button
                    onClick={() => setExpanded(expanded === campaign.id ? null : campaign.id)}
                    className="text-xs bg-gray-50 text-gray-700 px-3 py-1 rounded-lg hover:bg-gray-100 font-medium"
                  >
                    {expanded === campaign.id ? "Close" : "Manage"}
                  </button>
                  <button
                    onClick={() => deleteCampaign(campaign.id, campaign.name)}
                    title="Delete or archive this campaign"
                    className="text-xs bg-red-50 text-red-600 px-3 py-1 rounded-lg hover:bg-red-100"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {expanded === campaign.id && (
                <CampaignManager campaign={campaign} onChanged={loadCampaigns} />
              )}

              {["draft", "ready", "active", "paused"].includes(campaign.status) && (
                <CostEstimateCard campaignId={campaign.id} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
